import { useEffect, useMemo, useState } from "react";
import { Phone, Timer, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Filter, Radio } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";

/**
 * Admin + Office monitoring page for tracked call logs.
 * Auto-refreshes every 10 seconds. Shows LIVE indicator for in-progress calls.
 */
export default function CallLogsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [tcFilter, setTcFilter] = useState("all");
  const [fakeOnly, setFakeOnly] = useState(false);
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState({});
  const [telecallers, setTelecallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(Date.now());

  const load = async () => {
    try {
      const params = { date };
      if (tcFilter !== "all") params.telecaller_id = tcFilter;
      if (fakeOnly) params.fake_only = true;
      const r = await api.get("/admin/call-logs", { params });
      setCalls(r.data.calls || []);
      setStats(r.data.stats || {});
      setTelecallers(r.data.telecallers || []);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true); load();
    const t = setInterval(load, 10000);
    const s = setInterval(() => setTick(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(s); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, tcFilter, fakeOnly]);

  const liveCalls = useMemo(() => calls.filter(c => c.status === "in_progress"), [calls]);

  const bandBadge = (c) => {
    if (c.status === "in_progress") return { cls: "bg-red-500/20 text-red-300 border-red-500/40 animate-pulse", label: "LIVE" };
    if (c.is_fake) return { cls: "bg-red-500/20 text-red-300 border-red-500/30", label: "FAKE" };
    if (c.band === "short") return { cls: "bg-amber-500/20 text-amber-300 border-amber-500/30", label: "SHORT" };
    if (c.band === "valid") return { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", label: "VALID" };
    return { cls: "bg-slate-500/20 text-slate-300 border-slate-500/30", label: (c.band || c.status || "—").toUpperCase() };
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return "—"; } };
  const secAgo = (iso) => {
    if (!iso) return 0;
    return Math.max(0, Math.floor((tick - new Date(iso).getTime()) / 1000));
  };
  const fmtSec = (s) => s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;

  const cards = [
    { k: "total", label: "Today Total Calls", value: stats.total_calls ?? 0, icon: Phone, cls: "border-amber-500/40 bg-amber-500/10 text-amber-100" },
    { k: "valid", label: "Valid",             value: stats.valid ?? 0,        icon: CheckCircle2, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100" },
    { k: "short", label: "Short (verify)",    value: stats.short ?? 0,        icon: AlertTriangle, cls: "border-amber-500/40 bg-amber-500/10 text-amber-100" },
    { k: "fake",  label: "Fake",              value: stats.fake ?? 0,         icon: XCircle, cls: "border-red-500/40 bg-red-500/10 text-red-100" },
    { k: "talk",  label: "Total Talk Time",   value: stats.talk_time_formatted ?? "0s", icon: Timer, cls: "border-blue-500/40 bg-blue-500/10 text-blue-100" },
    { k: "avg",   label: "Avg Duration",      value: stats.avg_duration_formatted ?? "0s", icon: Timer, cls: "border-slate-600/50 bg-slate-800/60 text-slate-100" },
  ];

  return (
    <div className="space-y-4" data-testid="call-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading text-xl text-white flex items-center gap-2">
            <Phone className="w-5 h-5 text-amber-400"/> Call Logs
          </div>
          <div className="text-xs text-slate-500 mt-1">Auto-refresh every 10 seconds · IST day</div>
        </div>
        <Button size="sm" variant="ghost" onClick={load} data-testid="refresh-call-logs" className="text-slate-300 hover:bg-white/5">
          <RefreshCw className="w-4 h-4 mr-1.5"/> Refresh
        </Button>
      </div>

      {/* LIVE badge strip */}
      {liveCalls.length > 0 && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 space-y-1.5" data-testid="live-strip">
          {liveCalls.map(c => (
            <div key={c.id} className="flex items-center gap-2 text-sm text-red-200">
              <Radio className="w-4 h-4 text-red-400 animate-pulse"/>
              <span className="font-bold text-white">{c.telecaller_name}</span>
              <span className="text-slate-400">is on call with</span>
              <span className="font-bold text-amber-400">{c.lead_name}</span>
              <span className="text-slate-500">· Started {fmtSec(secAgo(c.start_time))} ago</span>
              <Badge className="ml-auto bg-red-500/30 text-red-200 border-red-500/50 text-[10px] uppercase tracking-widest">LIVE</Badge>
            </div>
          ))}
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {cards.map(c => (
          <div key={c.k} className={`rounded-xl border ${c.cls} px-3 py-3`} data-testid={`stat-${c.k}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
              <c.icon className="w-3 h-3"/> {c.label}
            </div>
            <div className="mt-1.5 font-display text-2xl">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap card-surface p-3">
        <Filter className="w-4 h-4 text-slate-500"/>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)}
          data-testid="filter-date" className="bg-slate-950 border-slate-700 text-white h-9 w-40"/>
        <Select value={tcFilter} onValueChange={setTcFilter}>
          <SelectTrigger data-testid="filter-telecaller" className="bg-slate-950 border-slate-700 text-white h-9 w-56">
            <SelectValue placeholder="All telecallers"/>
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-white">
            <SelectItem value="all">All telecallers</SelectItem>
            {telecallers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.phone}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-slate-300 ml-2 cursor-pointer">
          <input type="checkbox" checked={fakeOnly} onChange={e => setFakeOnly(e.target.checked)}
                 data-testid="filter-fake-only" className="accent-red-500"/>
          Fake only
        </label>
      </div>

      {/* Table */}
      <div className="card-surface overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5">
              <TableHead className="text-slate-400 text-xs">Time</TableHead>
              <TableHead className="text-slate-400 text-xs">Telecaller</TableHead>
              <TableHead className="text-slate-400 text-xs">Customer</TableHead>
              <TableHead className="text-slate-400 text-xs">Number</TableHead>
              <TableHead className="text-slate-400 text-xs">Duration</TableHead>
              <TableHead className="text-slate-400 text-xs">Outcome</TableHead>
              <TableHead className="text-slate-400 text-xs">Status</TableHead>
              <TableHead className="text-slate-400 text-xs">Remark</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (<TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>)}
            {!loading && calls.length === 0 && (<TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10" data-testid="calls-empty">No calls logged for this filter.</TableCell></TableRow>)}
            {calls.map(c => {
              const b = bandBadge(c);
              const isLive = c.status === "in_progress";
              const dur = isLive ? fmtSec(secAgo(c.start_time)) : (c.duration_formatted || "—");
              return (
                <TableRow key={c.id} className="border-white/5" data-testid={`call-row-${c.id}`}>
                  <TableCell className="text-slate-300 font-mono-alt text-xs">{fmt(c.start_time)}</TableCell>
                  <TableCell className="text-white">{c.telecaller_name}</TableCell>
                  <TableCell className="text-white font-semibold">{c.lead_name}</TableCell>
                  <TableCell className="text-slate-400 font-mono-alt text-xs">{c.customer_number}</TableCell>
                  <TableCell className={`font-mono-alt text-sm ${isLive ? "text-red-300" : c.is_fake ? "text-red-300" : c.band === "short" ? "text-amber-300" : "text-emerald-300"}`}>{dur}</TableCell>
                  <TableCell className="text-slate-300 text-sm">{c.outcome || "—"}</TableCell>
                  <TableCell><Badge className={b.cls + " text-[10px] uppercase tracking-widest"}>{b.label}</Badge></TableCell>
                  <TableCell className="text-slate-400 text-xs max-w-xs truncate" title={c.remark || ""}>{c.remark || "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
